/**
 * Service for fetching recommendations and related content for gifs.
 *
 * Responsibilities:
 * - Fetch related gifs based on tags.
 * - Fetch AI-based recommendations (by gif id) with cursor and optional filters.
 * - Fetch AI trending recommendations and update AI user state.
 * - Fetch Reddit-based recommendations and quick recommendations.
 */
import type { IGifList } from "../types";
import type { ApiClient } from "@/common/api/http/types";

export default class RecommendationsService {
  public constructor(private readonly client: ApiClient) {}

  async getRelatedGifs(params: {
    id: string;
    page: number;
    count: number;
  }): Promise<IGifList> {
    const url = `v2/recommend/tags/${params.id}?page=${params.page}&count=${params.count}`;

    return await this.client.get(url, {
      "X-Session-Id": await this.client.getSessionId(),
    });
  }

  async getAIGifRecommendations(params: {
    id: string;
    count: number;
    cursor: string | undefined;
    filter?: string;
    scenario?: string;
    tags?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();
    query.append("count", params.count.toString());

    if (params.cursor) {
      query.append("recommendation_id", params.cursor);
    }

    if (params.filter) {
      query.append("filter", params.filter);
    }

    if (params.scenario) {
      query.append("scenario", params.scenario);
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    const url = `v2/recommend/ai/${params.id}?${query.toString()}`;

    return await this.client.get(url, {
      "X-Session-Id": await this.client.getSessionId(),
    });
  }

  async getAITrendingRecommendations(props: {
    recommendationId: string | null;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.recommendationId) {
      query.append("recommendation_id", props.recommendationId);
    }

    return await this.client.get(
      `v2/recommend/ai/trending?${query.toString()}`,
      {
        "X-Session-Id": await this.client.getSessionId(),
      }
    );
  }

  async updateAIUser(): Promise<void> {
    return await this.client.patch(
      "v2/recommend/ai/user",
      {},
      {
        "X-Session-Id": await this.client.getSessionId(),
      }
    );
  }

  async getRedditRecs(params: {
    id: string;
    count: number;
    page: number;
    cursor?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();
    query.append("count", params.count.toString());
    query.append("page", params.page.toString());

    if (params.cursor) {
      query.append("recommendation_id", params.cursor);
    }

    return await this.client.get(
      `/v2/recommend/gifs/${params.id}/reddit?${query.toString()}`
    );
  }

  async getQuickRecs(params: { id: string }): Promise<IGifList> {
    return await this.client.get(
      `/v2/recommend/gifs/${params.id}/quick`
    );
  }
}
